<script setup lang="ts">
import type { Database } from '#shared/types/database.types'
import { logApiError } from '~/utils/logger'
import { sanitizeHtml } from '~/utils/sanitize'

type Faq = Database['public']['Tables']['vzw_faqs']['Row']

interface FaqsByCategory {
  [category: string]: Faq[]
}

// SEO Configuration
const { setSeoMeta, getBreadcrumbSchema } = useSeo()

setSeoMeta({
  title: 'Veelgestelde Vragen (FAQ)',
  description: 'Vind antwoorden op veelgestelde vragen over kinderkampen, inschrijvingen, betalingen en meer bij \'t SpeelFabrik. Alles wat je moet weten over onze kampen.',
  image: '/images/og-faq.jpg',
  keywords: ['FAQ', 'veelgestelde vragen', 'kinderkampen vragen', 'inschrijving kampen', 'betaling kampen'],
})

// Branded OG image
defineOgImage('SpeelFabrik', {
  title: 'Veelgestelde Vragen',
  description: 'Antwoorden op veelgestelde vragen over kinderkampen bij \'t SpeelFabrik.',
})

useSchemaOrg([
  getBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'FAQ', path: '/faq' },
  ]),
  {
    '@type': 'FAQPage',
    'name': 'Veelgestelde Vragen - \'t SpeelFabrik',
    'description': 'Antwoorden op veelgestelde vragen over kinderkampen',
    'url': 'https://www.tspeelfabrik.be/faq',
  },
])

definePageMeta({
  layout: 'front-page',
  public: true,
  pageTitle: {
    title: 'Veelgestelde Vragen',
    subtitle: 'Vind antwoorden op de meest gestelde vragen',
    icon: 'bx-help-circle',
    iconColor: 'primary',
  },
})

// Load FAQs
const { getFaqs } = useFaqs()
const faqs = ref<Faq[]>([])
const categories = ref<string[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const searchQuery = ref('')
const selectedCategory = ref<string | null>(null)

// Load data on mount
onMounted(async () => {
  loading.value = true
  error.value = null

  try {
    // Fetch active FAQs sorted by display_order
    const response = await getFaqs({
      isActive: true,
      sortBy: 'display_order',
      sortOrder: 'asc',
    })

    faqs.value = response.data || []

    // Extract unique categories
    const uniqueCategories = new Set<string>()

    faqs.value.forEach(faq => {
      if (faq.category) {
        uniqueCategories.add(faq.category)
      }
    })
    categories.value = Array.from(uniqueCategories).sort()
  }
  catch (err) {
    logApiError('/api/faqs', err)
    error.value = 'Er is een fout opgetreden bij het laden van de veelgestelde vragen.'
  }
  finally {
    loading.value = false
  }
})

// Filter FAQs by search and category
const filteredFaqs = computed(() => {
  let filtered = faqs.value

  // Filter by category
  if (selectedCategory.value) {
    filtered = filtered.filter(faq => faq.category === selectedCategory.value)
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()

    filtered = filtered.filter(
      faq =>
        faq.question.toLowerCase().includes(query)
        || faq.answer.toLowerCase().includes(query),
    )
  }

  return filtered
})

// Group FAQs by category
const faqsByCategory = computed<FaqsByCategory>(() => {
  const grouped: FaqsByCategory = {}

  filteredFaqs.value.forEach(faq => {
    const category = faq.category || 'Algemeen'
    if (!grouped[category]) {
      grouped[category] = []
    }
    grouped[category].push(faq)
  })

  return grouped
})

// Get sorted category keys
const sortedCategories = computed(() => {
  return Object.keys(faqsByCategory.value).sort()
})

// Clear filters
const clearFilters = () => {
  searchQuery.value = ''
  selectedCategory.value = null
}
</script>

<template>
  <VContainer>
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardText>
            <!-- Search and Filter -->
            <VRow class="mb-4">
              <VCol
                cols="12"
                md="8"
              >
                <VTextField
                  v-model="searchQuery"
                  label="Zoek een vraag"
                  prepend-inner-icon="bx-search"
                  variant="outlined"
                  clearable
                />
              </VCol>
              <VCol
                cols="12"
                md="4"
              >
                <VSelect
                  v-model="selectedCategory"
                  :items="[{ title: 'Alle categorieën', value: null }, ...categories.map(c => ({ title: c, value: c }))]"
                  label="Categorie"
                  variant="outlined"
                  clearable
                />
              </VCol>
            </VRow>

            <!-- Loading State -->
            <div v-if="loading">
              <div class="text-center pa-8">
                <VProgressCircular
                  indeterminate
                  color="primary"
                  size="64"
                  class="mb-4"
                />
                <p class="text-body-1">
                  Bezig met laden...
                </p>
              </div>
            </div>

            <!-- Error State -->
            <div v-else-if="error">
              <VAlert
                type="error"
                variant="tonal"
                closable
              >
                {{ error }}
              </VAlert>
            </div>

            <!-- No Results -->
            <div v-else-if="filteredFaqs.length === 0">
              <div class="text-center pa-8">
                <VIcon
                  icon="bx-search-alt"
                  size="64"
                  color="warning"
                  class="mb-4"
                />
                <h3 class="text-h5 mb-2">
                  Geen resultaten gevonden
                </h3>
                <p class="text-body-1 text-medium-emphasis mb-4">
                  Er zijn geen veelgestelde vragen die aan uw zoekcriteria voldoen.
                </p>
                <VBtn
                  color="primary"
                  variant="tonal"
                  @click="clearFilters"
                >
                  Filters wissen
                </VBtn>
              </div>
            </div>

            <!-- FAQ Content -->
            <div v-else>
              <div
                v-for="category in sortedCategories"
                :key="category"
                class="mb-8"
              >
                <!-- Category Header -->
                <h2 class="text-h4 mb-4">
                  <VIcon
                    icon="bx-folder"
                    class="me-2"
                    color="primary"
                  />
                  {{ category }}
                </h2>

                <!-- FAQ Items -->
                <VExpansionPanels
                  variant="accordion"
                  class="mb-4"
                >
                  <VExpansionPanel
                    v-for="faq in faqsByCategory[category]"
                    :key="faq.id"
                  >
                    <VExpansionPanelTitle class="text-h6">
                      <div class="d-flex align-center">
                        <VIcon
                          icon="bx-help-circle"
                          class="me-2"
                          color="info"
                          size="small"
                        />
                        {{ faq.question }}
                      </div>
                    </VExpansionPanelTitle>
                    <VExpansionPanelText>
                      <div
                        class="text-body-1 faq-richtext"
                        v-html="sanitizeHtml(faq.answer)"
                      />
                    </VExpansionPanelText>
                  </VExpansionPanel>
                </VExpansionPanels>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Contact CTA -->
    <VRow v-if="!loading && !error">
      <VCol cols="12">
        <VCard
          color="primary"
          variant="tonal"
        >
          <VCardText class="text-center pa-6">
            <VIcon
              icon="bx-envelope"
              size="48"
              class="mb-3"
            />
            <h3 class="text-h5 mb-2">
              Vraag niet gevonden?
            </h3>
            <p class="text-body-1 mb-4">
              Neem gerust contact met ons op als u uw vraag niet in onze lijst hebt gevonden.
            </p>
            <VBtn
              to="/contact"
              color="primary"
              size="large"
              variant="flat"
            >
              <VIcon
                icon="bx-send"
                class="me-2"
              />
              Contacteer ons
            </VBtn>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style scoped>
.faq-richtext :deep(p) {
  font-size: 0.95rem !important;
  line-height: 1.5 !important;
  margin-block: 0.35rem !important;
}

.faq-richtext :deep(p:first-child) {
  margin-block-start: 0 !important;
}

.faq-richtext :deep(p:last-child) {
  margin-block-end: 0 !important;
}
</style>
